import React, { useEffect, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import api from '../../services/api';
import {
  ManagementRequestResponse,
  ManagementRequestStatus,
} from '../../types';

const statusLabel: Record<ManagementRequestStatus, string> = {
  pending: '承認待ち',
  approved: '承認済み',
  rejected: '却下',
};

const statusColor: Record<ManagementRequestStatus, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

const ApprovalDashboard: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [companyRequests, setCompanyRequests] = useState<ManagementRequestResponse[]>([]);
  const [storeRequests, setStoreRequests] = useState<ManagementRequestResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 却下ダイアログ
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ type: 'company' | 'store'; id: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // 却下失敗時はダイアログが開いたままなので、背後ではなくダイアログ内にエラーを出す
  const [rejectError, setRejectError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [companyData, storeData] = await Promise.all([
        api.adminApproval.getCompanyRequests(),
        api.adminApproval.getStoreRequests(),
      ]);
      setCompanyRequests(companyData);
      setStoreRequests(storeData);
    } catch (error) {
      console.error('申請データの取得に失敗しました', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveCompany = async (requestId: string) => {
    setActionLoading(true);
    setMessage(null);
    try {
      await api.adminApproval.approveCompanyRequest(requestId);
      setMessage({ type: 'success', text: '企業登録申請を承認しました。' });
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: '承認処理に失敗しました。' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveStore = async (requestId: string) => {
    setActionLoading(true);
    setMessage(null);
    try {
      await api.adminApproval.approveStoreRequest(requestId);
      setMessage({ type: 'success', text: '店舗追加申請を承認しました。' });
      await fetchData();
    } catch (error) {
      setMessage({ type: 'error', text: '承認処理に失敗しました。' });
    } finally {
      setActionLoading(false);
    }
  };

  const openRejectDialog = (type: 'company' | 'store', id: string) => {
    setRejectTarget({ type, id });
    setRejectReason('');
    setRejectError(null);
    setRejectDialogOpen(true);
  };

  const handleReject = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    setActionLoading(true);
    setMessage(null);
    setRejectError(null);
    try {
      if (rejectTarget.type === 'company') {
        await api.adminApproval.rejectCompanyRequest(rejectTarget.id, rejectReason);
        setMessage({ type: 'success', text: '企業登録申請を却下しました。' });
      } else {
        await api.adminApproval.rejectStoreRequest(rejectTarget.id, rejectReason);
        setMessage({ type: 'success', text: '店舗追加申請を却下しました。' });
      }
      setRejectDialogOpen(false);
      await fetchData();
    } catch (error) {
      setRejectError('却下処理に失敗しました。');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCompanyCount = companyRequests.filter((r) => r.status === 'pending').length;
  const pendingStoreCount = storeRequests.filter((r) => r.status === 'pending').length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" sx={{ mb: 3 }}>
        承認管理
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{ mb: 3 }}
      >
        <Tab
          icon={<BusinessIcon />}
          iconPosition="start"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              企業登録申請
              {pendingCompanyCount > 0 && (
                <Chip label={pendingCompanyCount} size="small" color="warning" />
              )}
            </Box>
          }
        />
        <Tab
          icon={<StoreIcon />}
          iconPosition="start"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              店舗追加申請
              {pendingStoreCount > 0 && (
                <Chip label={pendingStoreCount} size="small" color="warning" />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* 企業登録申請タブ */}
      {tabIndex === 0 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>企業名</TableCell>
                    <TableCell>代表者</TableCell>
                    <TableCell>業種</TableCell>
                    <TableCell>店舗名</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>申請日</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        企業登録申請はありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    companyRequests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell>{req.company_name}</TableCell>
                        <TableCell>{req.representative_name || '-'}</TableCell>
                        <TableCell>{req.business_type || '-'}</TableCell>
                        <TableCell>{req.store_name || '(既存店舗)'}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel[req.status]}
                            size="small"
                            color={statusColor[req.status]}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(req.created_at).toLocaleDateString('ja-JP')}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          {req.status === 'pending' ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleApproveCompany(req.id)}
                                disabled={actionLoading}
                              >
                                承認
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => openRejectDialog('company', req.id)}
                                disabled={actionLoading}
                              >
                                却下
                              </Button>
                            </Box>
                          ) : req.status === 'rejected' && req.reject_reason ? (
                            <Typography variant="caption" color="error">
                              {req.reject_reason}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* 店舗追加申請タブ */}
      {tabIndex === 1 && (
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 520 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>店舗名</TableCell>
                    <TableCell>住所</TableCell>
                    <TableCell>電話番号</TableCell>
                    <TableCell>ステータス</TableCell>
                    <TableCell>申請日</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storeRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        店舗追加申請はありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    storeRequests.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell>{req.store_name}</TableCell>
                        <TableCell>{req.store_address || '-'}</TableCell>
                        <TableCell>{req.store_phone || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabel[req.status]}
                            size="small"
                            color={statusColor[req.status]}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(req.created_at).toLocaleDateString('ja-JP')}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          {req.status === 'pending' ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleApproveStore(req.id)}
                                disabled={actionLoading}
                              >
                                承認
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => openRejectDialog('store', req.id)}
                                disabled={actionLoading}
                              >
                                却下
                              </Button>
                            </Box>
                          ) : req.status === 'rejected' && req.reject_reason ? (
                            <Typography variant="caption" color="error">
                              {req.reject_reason}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* 却下理由入力ダイアログ */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>申請を却下</DialogTitle>
        <DialogContent>
          {rejectError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRejectError(null)}>
              {rejectError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            却下理由を入力してください。申請者に通知されます。
          </Typography>
          <TextField
            label="却下理由"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
            required
            disabled={actionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={actionLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={actionLoading || !rejectReason.trim()}
          >
            {actionLoading ? <CircularProgress size={20} /> : '却下する'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalDashboard;
